// REQ-P1-06b: 共通ヘッダー向けプロフィール参照API。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type AdminProfileResponse = NonNullable<
  paths['/admin/profile']['get']['responses'][200]['content']
>['application/json'];
type AdminProfileUpdateRequest = NonNullable<
  paths['/admin/profile']['put']['requestBody']
>['content']['application/json'];
type AdminProfileUpdateResponse = NonNullable<
  paths['/admin/profile']['put']['responses'][200]['content']
>['application/json'];
type AdminProfilePictureUpdateRequest = NonNullable<
  paths['/admin/profile-picture']['put']['requestBody']
>['content']['multipart/form-data'];
type AdminProfilePictureUpdateResponse = NonNullable<
  paths['/admin/profile-picture']['put']['responses'][200]['content']
>['application/json'];
type AdminProfilePictureDeleteResponse = NonNullable<
  paths['/admin/profile-picture']['delete']['responses'][200]['content']
>['application/json'];
type AdminFullNameUpdateRequest = NonNullable<
  paths['/admin/profile/full-name']['put']['requestBody']
>['content']['application/json'];
type AdminFullNameUpdateResponse = NonNullable<
  paths['/admin/profile/full-name']['put']['responses'][200]['content']
>['application/json'];
type ArtistProfileResponse = NonNullable<
  paths['/artist/profile/me']['get']['responses'][200]['content']
>['application/json'];
type ArtistProfileDetailResponse = NonNullable<
  paths['/artists/{id}']['get']['responses'][200]['content']
>['application/json'];

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const message = (parsed as Record<string, unknown>).message || (parsed as Record<string, unknown>).error;
        if (typeof message === 'string') return message;
      }
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

export async function getCurrentAdminProfile(): Promise<AdminProfileResponse | null> {
  const { data, response } = await api.GET('/admin/profile');
  if (!response.ok || !data) return null;
  return data as AdminProfileResponse;
}

export async function getCurrentAdminProfileOrThrow(): Promise<AdminProfileResponse> {
  const { data, error, response } = await api.GET('/admin/profile');
  if (!response.ok || !data) throwApiError(response.status, error, 'プロフィール情報の取得に失敗しました');
  return data as AdminProfileResponse;
}

export async function updateCurrentAdminProfile(
  body: AdminProfileUpdateRequest & Record<string, unknown>
): Promise<AdminProfileUpdateResponse> {
  const { data, error, response } = await api.PUT('/admin/profile', {
    body: body as AdminProfileUpdateRequest,
  });
  if (!response.ok || !data) throwApiError(response.status, error, 'プロフィールの更新に失敗しました');
  return data as AdminProfileUpdateResponse;
}

export async function updateCurrentAdminProfilePicture(
  body: FormData
): Promise<AdminProfilePictureUpdateResponse> {
  const { data, error, response } = await api.PUT('/admin/profile-picture', {
    body: body as unknown as AdminProfilePictureUpdateRequest,
  });
  if (!response.ok || !data) throwApiError(response.status, error, 'プロフィール画像の更新に失敗しました');
  return data as AdminProfilePictureUpdateResponse;
}

export async function deleteCurrentAdminProfilePicture(): Promise<AdminProfilePictureDeleteResponse> {
  const { data, error, response } = await api.DELETE('/admin/profile-picture');
  if (!response.ok || !data) throwApiError(response.status, error, 'プロフィール画像の削除に失敗しました');
  return data as AdminProfilePictureDeleteResponse;
}

export async function updateCurrentAdminFullName(
  body: AdminFullNameUpdateRequest
): Promise<AdminFullNameUpdateResponse> {
  const { data, error, response } = await api.PUT('/admin/profile/full-name', { body });
  if (!response.ok || !data) throwApiError(response.status, error, 'フルネームの登録に失敗しました。');
  return data as AdminFullNameUpdateResponse;
}

export async function getCurrentArtistProfile(): Promise<ArtistProfileResponse | null> {
  const { data, response } = await api.GET('/artist/profile/me');
  if (!response.ok || !data) return null;
  return data as ArtistProfileResponse;
}

export async function getArtistProfileDetail(
  id: string
): Promise<ArtistProfileDetailResponse | null> {
  const { data, response } = await api.GET('/artists/{id}', {
    params: { path: { id } },
  });
  if (!response.ok || !data) return null;
  return data as ArtistProfileDetailResponse;
}
